import { useState, useEffect } from "react";

/* ─── Types ──────────────────────────────────────────────────── */
export type Invitation = {
  id: string;
  tenant_id: string;
  job_id: string | null;
  client_id: string;
  contract_id: string | null;
  portal_token: string | null;
  // design mode: 'template' | 'photo' | 'custom'
  design_mode: string;
  template_id: string | null;
  custom_template_id: string | null;
  photo_url: string | null;
  // titles
  title: string;
  event_name: string | null;
  event_title: string | null;
  host_names: string | null;
  event_type: string;
  // date/time
  event_date: string | null;
  event_time: string | null;
  // venue (reception)
  event_location: string | null;
  venue_name: string | null;
  venue_address: string | null;
  google_maps_url: string | null;
  // ceremony
  ceremony_name: string | null;
  ceremony_location: string | null;
  ceremony_time: string | null;
  ceremony_address: string | null;
  ceremony_maps_url: string | null;
  // reception (alias fields from DB)
  reception_name: string | null;
  reception_address: string | null;
  reception_maps_url: string | null;
  // extra
  dress_code: string | null;
  gift_registry_url: string | null;
  custom_message: string | null;
  welcome_message: string | null;
  thank_you_message: string | null;
  // style
  text_color: string;
  text_style: string;
  theme_key: string;
  // media
  cover_image_url: string | null;
  music_url: string | null;
  music_title: string | null;
  client_music_url: string | null;
  client_music_name: string | null;
  gallery_urls: string[];
  // settings
  show_guests: boolean;
  public_slug: string;
  rsvp_enabled: boolean;
  is_published: boolean;
  invitation_mode: string | null;
  custom_template_url: string | null;
  created_at: string;
  updated_at: string;
};

export type SimpleClient = { id: string; full_name: string; email: string };
export type SimpleJob    = { id: string; title: string };

const EVENT_TYPES = [
  "Wedding", "XV Años", "Baptism", "Graduation", "Birthday",
  "Baby Shower", "Engagement", "Anniversary", "Corporate Event", "Other",
];

const THEMES = [
  { key: "classic",   label: "Classic Elegant" },
  { key: "modern",    label: "Modern Minimal" },
  { key: "floral",    label: "Floral Garden" },
  { key: "dark",      label: "Dark Luxury" },
  { key: "celestial", label: "Celestial" },
];

/* ─── Slug generator ─────────────────────────────────────────── */
function generateSlug(title: string) {
  return (
    title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 40) +
    "-" +
    Math.random().toString(36).slice(2, 7)
  );
}

/* ─── Props ──────────────────────────────────────────────────── */
interface Props {
  invitation: Invitation | null;
  isNew: boolean;
  clients: SimpleClient[];
  jobs: SimpleJob[];
  onClose: () => void;
  onSave: (data: Invitation) => void;
}

/* ─── Tab type ───────────────────────────────────────────────── */
type Tab = "basic" | "event" | "media" | "settings";

/* ─── Modal ──────────────────────────────────────────────────── */
export default function InvitationModal({
  invitation,
  isNew,
  clients,
  jobs,
  onClose,
  onSave,
}: Props) {
  const blank: Invitation = {
    id: "",
    tenant_id: "",
    job_id: null,
    client_id: "",
    contract_id: null,
    portal_token: null,
    design_mode: "template",
    template_id: null,
    custom_template_id: null,
    photo_url: null,
    title: "",
    event_name: null,
    event_title: null,
    host_names: null,
    event_type: "Wedding",
    event_date: null,
    event_time: null,
    event_location: null,
    venue_name: null,
    venue_address: null,
    google_maps_url: null,
    ceremony_name: null,
    ceremony_location: null,
    ceremony_time: null,
    ceremony_address: null,
    ceremony_maps_url: null,
    reception_name: null,
    reception_address: null,
    reception_maps_url: null,
    dress_code: null,
    gift_registry_url: null,
    custom_message: null,
    welcome_message: null,
    thank_you_message: null,
    text_color: "#ffffff",
    text_style: "elegante",
    theme_key: "classic",
    cover_image_url: null,
    music_url: null,
    music_title: null,
    client_music_url: null,
    client_music_name: null,
    gallery_urls: [],
    show_guests: false,
    public_slug: "",
    rsvp_enabled: true,
    is_published: false,
    invitation_mode: null,
    custom_template_url: null,
    created_at: "",
    updated_at: "",
  };

  const [form, setForm] = useState<Invitation>(invitation ?? blank);
  const [activeTab, setActiveTab] = useState<Tab>("basic");
  const [galleryInput, setGalleryInput] = useState("");

  useEffect(() => {
    if (isNew) {
      setForm(blank);
      setActiveTab("basic");
    } else if (invitation) {
      setForm({
        ...invitation,
        gallery_urls: Array.isArray(invitation.gallery_urls) ? invitation.gallery_urls : [],
      });
      setActiveTab("basic");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation, isNew]);

  const set = <K extends keyof Invitation>(key: K, val: Invitation[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const handleTitleChange = (val: string) => {
    setForm((prev) => ({
      ...prev,
      title: val,
      public_slug: isNew ? generateSlug(val) : prev.public_slug,
    }));
  };

  const addGalleryUrl = () => {
    const url = galleryInput.trim();
    if (!url) return;
    setForm((prev) => ({ ...prev, gallery_urls: [...prev.gallery_urls, url] }));
    setGalleryInput("");
  };

  const removeGalleryUrl = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      gallery_urls: prev.gallery_urls.filter((_, i) => i !== idx),
    }));
  };

  const isValid =
    form.title.trim() &&
    form.client_id &&
    form.event_type &&
    form.public_slug.trim();

  const TABS: { id: Tab; label: string; icon: string }[] = [
    { id: "basic",    label: "Basic",    icon: "ri-information-line" },
    { id: "event",    label: "Event",    icon: "ri-calendar-event-line" },
    { id: "media",    label: "Media",    icon: "ri-image-2-line" },
    { id: "settings", label: "Settings", icon: "ri-settings-3-line" },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "New Invitation" : "Edit Invitation"}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">Digital invitation card</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-slate-100 shrink-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                activeTab === t.id
                  ? "text-[#C8A96E] border-[#C8A96E]"
                  : "text-slate-400 border-transparent hover:text-slate-600"
              }`}
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${t.icon} text-xs`}></i>
              </span>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* ── TAB: BASIC ── */}
          {activeTab === "basic" && (
            <>
              {/* Client + Job */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">
                    Client <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={form.client_id}
                    onChange={(e) => set("client_id", e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer"
                  >
                    <option value="">Select client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">
                    Linked Job <span className="text-slate-300">(optional)</span>
                  </label>
                  <select
                    value={form.job_id ?? ""}
                    onChange={(e) => set("job_id", e.target.value || null)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer"
                  >
                    <option value="">None</option>
                    {jobs.map((j) => (
                      <option key={j.id} value={j.id}>{j.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Internal title */}
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">
                  Internal Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Ana &amp; Carlos Wedding"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
                />
                <p className="text-slate-400 text-xs mt-1">Used internally. The public title is set in the Event tab.</p>
              </div>

              {/* Event title (public) + Host names */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">
                    Event Title <span className="text-slate-300">(public)</span>
                  </label>
                  <input
                    type="text"
                    value={form.event_title ?? ""}
                    onChange={(e) => set("event_title", e.target.value || null)}
                    placeholder="Ana &amp; Carlos"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">
                    Host Names
                  </label>
                  <input
                    type="text"
                    value={form.host_names ?? ""}
                    onChange={(e) => set("host_names", e.target.value || null)}
                    placeholder="Mr. &amp; Mrs. García"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
                  />
                </div>
              </div>

              {/* Event type */}
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">
                  Event Type <span className="text-red-400">*</span>
                </label>
                <select
                  value={form.event_type}
                  onChange={(e) => set("event_type", e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer"
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Slug */}
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">
                  Public URL Slug <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-0 bg-slate-50 border border-slate-200 rounded-lg overflow-hidden focus-within:border-[#C8A96E]/50 transition-colors">
                  <span className="px-3 py-2 text-slate-400 text-xs whitespace-nowrap border-r border-slate-200">
                    /invite/
                  </span>
                  <input
                    type="text"
                    value={form.public_slug}
                    onChange={(e) =>
                      set(
                        "public_slug",
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, "-")
                          .replace(/-+/g, "-"),
                      )
                    }
                    placeholder="ana-carlos-wedding-2024"
                    className="flex-1 bg-transparent px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {/* ── TAB: EVENT ── */}
          {activeTab === "event" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Event Date</label>
                  <input type="date" value={form.event_date ? form.event_date.slice(0, 10) : ""} onChange={(e) => set("event_date", e.target.value || null)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors" />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Event Time</label>
                  <input type="text" value={form.event_time ?? ""} onChange={(e) => set("event_time", e.target.value || null)} placeholder="6:00 PM" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Venue Name</label>
                <input type="text" value={form.venue_name ?? ""} onChange={(e) => set("venue_name", e.target.value || null)} placeholder="Jardín El Encanto" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
              </div>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Venue Address</label>
                <input type="text" value={form.venue_address ?? ""} onChange={(e) => set("venue_address", e.target.value || null)} placeholder="Av. Principal 123, Ciudad" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
              </div>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Google Maps URL</label>
                <input type="url" value={form.google_maps_url ?? ""} onChange={(e) => set("google_maps_url", e.target.value || null)} placeholder="https://maps.google.com/..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Dress Code</label>
                  <input type="text" value={form.dress_code ?? ""} onChange={(e) => set("dress_code", e.target.value || null)} placeholder="Formal / Black Tie" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Gift Registry URL</label>
                  <input type="url" value={form.gift_registry_url ?? ""} onChange={(e) => set("gift_registry_url", e.target.value || null)} placeholder="https://..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
                </div>
              </div>
            </>
          )}

          {/* ── TAB: MEDIA ── */}
          {activeTab === "media" && (
            <>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Cover Image URL</label>
                <input type="url" value={form.cover_image_url ?? ""} onChange={(e) => set("cover_image_url", e.target.value || null)} placeholder="https://..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
                {form.cover_image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden h-28 bg-slate-100">
                    <img src={form.cover_image_url} alt="Cover preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Background Music URL</label>
                <input type="url" value={form.music_url ?? ""} onChange={(e) => set("music_url", e.target.value || null)} placeholder="https://... (mp3, Spotify embed, etc.)" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
                <p className="text-slate-400 text-xs mt-1">Direct MP3 link or embed URL for background music.</p>
              </div>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-1.5">Gallery Images</label>
                <div className="flex gap-2 mb-3">
                  <input type="url" value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGalleryUrl()} placeholder="https://... (press Enter to add)" className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors" />
                  <button onClick={addGalleryUrl} className="px-3 py-2 bg-[#C8A96E]/10 hover:bg-[#C8A96E]/20 text-[#C8A96E] rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
                  </button>
                </div>
                {form.gallery_urls.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {form.gallery_urls.map((url, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden h-20 bg-slate-100">
                        <img src={url} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover object-top" />
                        <button onClick={() => removeGalleryUrl(idx)} className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-black/50 text-white hover:text-red-300 rounded-full opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-xs">No gallery images added yet.</p>
                )}
              </div>
            </>
          )}

          {/* ── TAB: SETTINGS ── */}
          {activeTab === "settings" && (
            <>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Theme</label>
                <div className="grid grid-cols-5 gap-2">
                  {THEMES.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => set("theme_key", t.key)}
                      className={`py-2 px-2 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap text-center ${
                        form.theme_key === t.key
                          ? "border-[#C8A96E] bg-[#C8A96E]/10 text-[#C8A96E]"
                          : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-4 pt-2">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm text-[#0f172a] font-medium">Enable RSVP</p>
                    <p className="text-xs text-slate-400 mt-0.5">Allow guests to confirm attendance</p>
                  </div>
                  <div
                    onClick={() => set("rsvp_enabled", !form.rsvp_enabled)}
                    className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer shrink-0 ${
                      form.rsvp_enabled ? "bg-[#C8A96E]" : "bg-slate-200"
                    }`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${ form.rsvp_enabled ? "translate-x-5" : "translate-x-0.5" }`} />
                  </div>
                </label>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 shrink-0 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  activeTab === t.id ? "bg-[#C8A96E]" : "bg-slate-200 hover:bg-slate-300"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              Cancel
            </button>
            <button
              onClick={() => isValid && onSave(form)}
              disabled={!isValid}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line"></i></span>
              {isNew ? "Create Invitation" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
